import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShoppingBasket, Trash2, Loader2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { getOptimizedImageUrl } from "@/lib/image-utils";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export function CartDrawer() {
  const { items, removeItem, clearCart, totalCount, totalPrice } = useCart();
  const [checkingOut, setCheckingOut] = useState(false);
  const { toast } = useToast();
  const [open, setOpen] = useState(false);

  const handleCheckout = async () => {
    if (items.length === 0) return;
    setCheckingOut(true);
    try {
      // For multiple items, checkout the first (artworks are unique originals — buyers pick one at a time via this flow)
      // In future this can be extended to a multi-line Stripe session
      if (items.length === 1) {
        const item = items[0];
        const { data, error } = await supabase.functions.invoke("create-artwork-payment", {
          body: {
            artworkId: item.artwork.id,
            artworkTitle: item.artwork.title,
            priceInPence: Math.round((item.artwork.price ?? 0) * 100),
            imageUrl: item.artwork.image_url,
          },
        });
        if (error || !data?.url) throw error ?? new Error("No checkout URL returned");
        window.open(data.url, "_blank");
        clearCart();
        setOpen(false);
      } else {
        // Multiple items — checkout each in sequence via one Stripe session per item isn't ideal;
        // here we redirect to the first item and note the rest.
        toast({
          title: "Multiple items",
          description: "Please checkout one artwork at a time for now.",
        });
      }
    } catch {
      toast({ title: "Checkout error", description: "Could not start checkout. Please try again.", variant: "destructive" });
    } finally {
      setCheckingOut(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button className="relative p-2 rounded-full hover:bg-muted transition-colors" aria-label="Open basket">
          <ShoppingBasket className="h-5 w-5 text-foreground" />
          {totalCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-accent text-accent-foreground text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center leading-none">
              {totalCount}
            </span>
          )}
        </button>
      </SheetTrigger>

      <SheetContent className="flex flex-col gap-0 w-full sm:max-w-sm bg-card">
        <SheetHeader className="px-6 py-4 border-b border-border">
          <SheetTitle className="font-bubblegum text-2xl text-card-foreground flex items-center gap-2">
            <ShoppingBasket className="h-5 w-5" /> Basket
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-2 text-muted-foreground px-6">
            <ShoppingBasket className="h-12 w-12 opacity-30" />
            <p className="font-fredoka text-lg">Your basket is empty 🛒</p>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-4">
              {items.map(({ artwork }) => (
                <div key={artwork.id} className="flex items-center gap-3">
                  <img
                    src={getOptimizedImageUrl(artwork.image_url, { width: 64 })}
                    alt={artwork.title}
                    className="h-16 w-16 object-cover rounded-lg border border-border flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-bubblegum text-card-foreground truncate">{artwork.title}</p>
                    <Badge className="bg-adele-teal text-primary-foreground mt-1">
                      £{Number(artwork.price).toFixed(2)}
                    </Badge>
                  </div>
                  <button
                    onClick={() => removeItem(artwork.id)}
                    className="text-muted-foreground hover:text-destructive transition-colors p-1"
                    aria-label="Remove from basket"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="border-t border-border px-6 py-4 flex flex-col gap-3">
              <div className="flex justify-between font-fredoka text-lg text-card-foreground">
                <span>Total</span>
                <span className="font-bold">£{totalPrice.toFixed(2)}</span>
              </div>
              <Button
                className="w-full font-fredoka text-base"
                onClick={handleCheckout}
                disabled={checkingOut}
              >
                {checkingOut ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Checkout
              </Button>
              <button
                onClick={clearCart}
                className="text-center text-sm text-muted-foreground hover:text-destructive transition-colors font-fredoka"
              >
                Clear basket
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
